import React, { useEffect, useState } from 'react';
import { abuseDetectionAdminService, AbuseReport } from '../../services/abuse-detection.admin.service';
import './AbuseReportPage.css';

export function AbuseReportPage() {
  const [report, setReport] = useState<AbuseReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await abuseDetectionAdminService.getAbuseReport();
      setReport(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="abuse-report-loading">Cargando reporte...</div>;
  if (error) return <div className="abuse-report-error">Error: {error}</div>;
  if (!report) return null;

  return (
    <div className="abuse-report-page">
      <h1>🔍 Reporte de Detección de Abuso</h1>

      {/* Summary */}
      <div className="abuse-summary">
        <div className="summary-card">
          <h3>📊 Resumen</h3>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-value">{report.summary.total_devices_flagged}</span>
              <span className="stat-label">Dispositivos Sospechosos</span>
            </div>
            <div className="stat">
              <span className="stat-value">{report.summary.total_users_flagged}</span>
              <span className="stat-label">Usuarios Sospechosos</span>
            </div>
            <div className="stat">
              <span className="stat-value">{report.summary.total_suspicious_votes}</span>
              <span className="stat-label">Votos Sospechosos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Suspicious Devices */}
      <div className="abuse-section">
        <h2>🖥️ Dispositivos con Múltiples Cuentas</h2>
        <p className="section-description">
          Dispositivos desde los cuales han votado 2 o más cuentas diferentes (posible creación de múltiples cuentas)
        </p>

        {report.suspicious_devices.length === 0 ? (
          <div className="no-data">✅ No se detectaron dispositivos sospechosos</div>
        ) : (
          <div className="devices-list">
            {report.suspicious_devices.map((device) => (
              <div key={device.device_fingerprint} className="device-card">
                <div className="device-header">
                  <span className="device-id">
                    🔑 {device.device_fingerprint.substring(0, 16)}...
                  </span>
                  <span className="badge badge-warning">
                    {device.user_count} cuentas
                  </span>
                </div>

                <div className="device-stats">
                  <span>📊 {device.total_votes} votos totales</span>
                  <span>📋 {device.polls_voted.length} encuestas</span>
                </div>

                <div className="users-list">
                  <h4>Cuentas que votaron desde este dispositivo:</h4>
                  {device.users.map((user) => (
                    <div key={user.user_id} className="user-row">
                      <div className="user-info">
                        <strong>{user.display_name || user.username}</strong>
                        <span className="user-meta">@{user.username}</span>
                      </div>
                      <div className="user-stats">
                        <span className="badge">{user.vote_count} votos</span>
                        <span className="timestamp">
                          {new Date(user.first_vote).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Suspicious Users */}
      <div className="abuse-section">
        <h2>👤 Usuarios con Múltiples Dispositivos</h2>
        <p className="section-description">
          Usuarios que han votado desde 3 o más dispositivos diferentes (posible bot o automatización)
        </p>

        {report.suspicious_users.length === 0 ? (
          <div className="no-data">✅ No se detectaron usuarios sospechosos</div>
        ) : (
          <div className="users-list">
            {report.suspicious_users.map((user) => (
              <div key={user.user_id} className="user-card">
                <div className="user-header">
                  <div className="user-info">
                    <strong>{user.display_name || user.username}</strong>
                    <span className="user-meta">@{user.username}</span>
                  </div>
                  <span className="badge badge-danger">
                    {user.device_count} dispositivos
                  </span>
                </div>

                <div className="user-stats">
                  <div className="stat-row">
                    <span>📊 Total de votos:</span>
                    <strong>{user.total_votes}</strong>
                  </div>
                  <div className="stat-row">
                    <span>⏱️ Edad de cuenta:</span>
                    <strong>{user.account_age_hours}h</strong>
                  </div>
                  <div className="stat-row">
                    <span>📈 Votos/hora:</span>
                    <strong>{user.votes_per_hour.toFixed(2)}</strong>
                  </div>
                </div>

                <div className="devices-info">
                  <h4>Dispositivos utilizados:</h4>
                  <div className="devices-chips">
                    {user.devices.map((device, idx) => (
                      <span key={device} className="device-chip">
                        Device {idx + 1}: {device.substring(0, 8)}...
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="abuse-footer">
        <p>
          ℹ️ Este reporte es informativo. Revisa manualmente antes de tomar acciones.
        </p>
        <button onClick={loadReport} className="refresh-button">
          🔄 Actualizar Reporte
        </button>
      </div>
    </div>
  );
}
